// ============================================================================
// OfflineBanner - Network Status & Sync Progress Indicator
// EduPath Africa v2.0 - Sprint 3: Offline Assessment Infrastructure
// ============================================================================
//
// FILE: src/components/shared/OfflineBanner.tsx
//
// Displays:
//   - Offline warning banner when no connectivity
//   - "Slow connection" banner when latency is high
//   - Sync progress bar when items are being uploaded
//   - "X items pending sync" badge when back online with queued items
//
// Mount this component in your main layout (AppLayout.tsx or similar)
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  WifiOff, Wifi, CloudOff, RefreshCw, Check, AlertTriangle,
  Loader2, Cloud,
} from 'lucide-react';
import useNetworkStatus from '@/hooks/useNetworkStatus';
import type { SyncProgress } from '@/services/syncService';

// ============================================================================
// OfflineBanner Component
// ============================================================================

const OfflineBanner: React.FC = () => {
  const { isOnline, connectionQuality, pendingSyncCount } = useNetworkStatus();
  const [syncProgress, setSyncProgress] = useState<SyncProgress | null>(null);
  const [showSyncSuccess, setShowSyncSuccess] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Listen for sync events
  useEffect(() => {
    const handleSyncStarted = (e: Event) => {
      const detail = (e as CustomEvent).detail as SyncProgress;
      setSyncProgress(detail);
      setShowSyncSuccess(false);
    };

    const handleSyncProgress = (e: Event) => {
      const detail = (e as CustomEvent).detail as SyncProgress;
      setSyncProgress(detail);
    };

    const handleSyncComplete = (e: Event) => {
      const detail = (e as CustomEvent).detail as SyncProgress;
      setSyncProgress(null);
      if (detail.completed > 0) {
        setShowSyncSuccess(true);
        setTimeout(() => setShowSyncSuccess(false), 5000);
      }
    };

    window.addEventListener('sync:started', handleSyncStarted);
    window.addEventListener('sync:progress', handleSyncProgress);
    window.addEventListener('sync:complete', handleSyncComplete);

    return () => {
      window.removeEventListener('sync:started', handleSyncStarted);
      window.removeEventListener('sync:progress', handleSyncProgress);
      window.removeEventListener('sync:complete', handleSyncComplete);
    };
  }, []);

  // Reset dismissed state when status changes
  useEffect(() => {
    setDismissed(false);
  }, [isOnline]);

  // Don't show anything if online, no sync, and no pending items
  if (isOnline && !syncProgress && !showSyncSuccess && pendingSyncCount === 0 && connectionQuality === 'good') {
    return null;
  }

  if (dismissed && isOnline) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-50">
      {/* Offline Banner */}
      {!isOnline && (
        <div className="bg-amber-600 text-white px-4 py-2.5 flex items-center justify-between shadow-lg">
          <div className="flex items-center gap-2.5">
            <WifiOff className="w-5 h-5 flex-shrink-0" />
            <div>
              <span className="font-medium text-sm">You&apos;re offline</span>
              <span className="text-amber-100 text-xs ml-2">
                Your assessment progress is saved locally and will sync when you reconnect.
              </span>
            </div>
          </div>
          <button
            onClick={() => setDismissed(true)}
            className="text-amber-200 hover:text-white text-xs underline ml-4 flex-shrink-0"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Slow Connection Warning */}
      {isOnline && connectionQuality === 'slow' && !syncProgress && (
        <div className="bg-yellow-500 text-yellow-900 px-4 py-2 flex items-center gap-2.5 shadow">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">
            Slow connection detected. Your work is being saved locally as a backup.
          </span>
          <button
            onClick={() => setDismissed(true)}
            className="text-yellow-700 hover:text-yellow-900 text-xs underline ml-auto flex-shrink-0"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Sync Progress Bar */}
      {syncProgress && syncProgress.inProgress && (
        <div className="bg-blue-600 text-white px-4 py-2 shadow">
          <div className="flex items-center gap-2.5">
            <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
            <span className="text-sm font-medium">
              Syncing your assessment data...
            </span>
            <span className="text-blue-200 text-xs ml-auto">
              {syncProgress.completed}/{syncProgress.total} items
            </span>
          </div>
          <div className="mt-1.5 h-1 bg-blue-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-300 rounded-full transition-all duration-500"
              style={{
                width: syncProgress.total > 0
                  ? `${(syncProgress.completed / syncProgress.total) * 100}%`
                  : '0%'
              }}
            />
          </div>
        </div>
      )}

      {/* Sync Success */}
      {showSyncSuccess && (
        <div className="bg-green-600 text-white px-4 py-2 flex items-center gap-2.5 shadow animate-slide-down">
          <Check className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">All assessment data synced successfully!</span>
        </div>
      )}

      {/* Pending Items Badge (when online but items not yet synced) */}
      {isOnline && !syncProgress && !showSyncSuccess && pendingSyncCount > 0 && (
        <div className="bg-indigo-600 text-white px-4 py-2 flex items-center justify-between shadow">
          <div className="flex items-center gap-2.5">
            <Cloud className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm">
              {pendingSyncCount} assessment{pendingSyncCount !== 1 ? 's' : ''} waiting to sync
            </span>
          </div>
          <button
            onClick={() => {
              import('@/services/syncService').then((m) => m.syncService.processQueue());
            }}
            className="flex items-center gap-1.5 bg-indigo-500 hover:bg-indigo-400 px-3 py-1 rounded text-xs font-medium transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Sync Now
          </button>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// OfflineIndicator - Small dot indicator for headers/nav
// ============================================================================

export const OfflineIndicator: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { isOnline, pendingSyncCount } = useNetworkStatus();

  if (isOnline && pendingSyncCount === 0) return null;

  return (
    <div className={`flex items-center gap-1.5 ${className}`}>
      {!isOnline ? (
        <>
          <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
          <span className="text-xs text-amber-600 font-medium">Offline</span>
        </>
      ) : pendingSyncCount > 0 ? (
        <>
          <div className="w-2 h-2 rounded-full bg-indigo-500" />
          <span className="text-xs text-indigo-600 font-medium">{pendingSyncCount} pending</span>
        </>
      ) : null}
    </div>
  );
};

export default OfflineBanner;
